/**
 * Ticket form — absorbed from the old ContactHQ modal, now with auto-captured
 * submission context so support never has to ask "what page / what browser?".
 */
import React from 'react';
import { CheckCircle, Image as ImageIcon, Loader2, Send, Trash2 } from 'lucide-react';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { storage } from '../../../services/firebase/config';
import { useAuth } from '../../../contexts/AuthContext';
import { useToast } from '../../../contexts/ToastContext';
import { createTicket, getTicketById } from '../../../services/firebase/ticketService';
import {
  TicketPriority,
  TicketStatus,
  TicketType,
  type SupportTicket,
} from '../../../types/ticket.types';
import { captureSubmissionContext } from '../../../utils/captureSubmissionContext';

const TYPE_OPTIONS: { value: TicketType; label: string }[] = [
  { value: TicketType.SOFTWARE, label: 'Software issue' },
  { value: TicketType.ADMIN, label: 'Admin / account' },
  { value: TicketType.BILLING, label: 'Billing' },
  { value: TicketType.FEATURE_REQUEST, label: 'Feature request' },
  { value: TicketType.OTHER, label: 'Other' },
];

interface ScreenshotFile {
  file: File;
  preview: string;
  id: string;
}

interface Props {
  initialType?: TicketType;
}

export const TicketFormTab: React.FC<Props> = ({ initialType }) => {
  const { userProfile } = useAuth();
  const { showToast } = useToast();
  const [type, setType] = React.useState<TicketType | ''>(initialType ?? '');
  const [subject, setSubject] = React.useState('');
  const [description, setDescription] = React.useState('');
  const [priority, setPriority] = React.useState<TicketPriority>(TicketPriority.MEDIUM);
  const [screenshots, setScreenshots] = React.useState<ScreenshotFile[]>([]);
  const [submitting, setSubmitting] = React.useState(false);
  const [submitted, setSubmitted] = React.useState<SupportTicket | null>(null);
  const fileInputRef = React.useRef<HTMLInputElement | null>(null);

  const handleFiles = (files: FileList | null): void => {
    if (!files) return;
    const maxSize = 5 * 1024 * 1024;
    const next: ScreenshotFile[] = [];
    Array.from(files).forEach((file) => {
      if (!file.type.startsWith('image/')) {
        showToast('error', `${file.name} is not an image file`);
        return;
      }
      if (file.size > maxSize) {
        showToast('error', `${file.name} is too large. Maximum size is 5MB`);
        return;
      }
      next.push({ file, preview: URL.createObjectURL(file), id: Math.random().toString(36).slice(2, 11) });
    });
    if (next.length > 0) setScreenshots((prev) => [...prev, ...next]);
  };

  const submit = async (): Promise<void> => {
    if (!userProfile) return;
    if (!type) return void showToast('error', 'Please select a support type');
    if (!subject.trim()) return void showToast('error', 'Please enter a subject');
    if (!description.trim()) return void showToast('error', 'Please describe your issue');

    setSubmitting(true);
    try {
      let attachmentUrls: string[] = [];
      if (screenshots.length > 0) {
        try {
          attachmentUrls = await Promise.all(
            screenshots.map(async (s) => {
              const fileName = `ticket_${Date.now()}_${s.file.name}`;
              const storageRefObj = ref(storage, `tickets/attachments/${fileName}`);
              const snapshot = await uploadBytes(storageRefObj, s.file);
              return await getDownloadURL(snapshot.ref);
            }),
          );
        } catch (uploadError) {
          console.error('Error uploading screenshots:', uploadError);
          showToast('warning', 'Some screenshots could not be uploaded, but the ticket will be created');
        }
      }

      const ticketId = await createTicket({
        type,
        subject: subject.trim(),
        description: description.trim(),
        priority,
        status: TicketStatus.OPEN,
        attachments: attachmentUrls,
        userId: userProfile.id,
        userName: `${userProfile.firstName || ''} ${userProfile.lastName || ''}`.trim(),
        userEmail: userProfile.email || '',
        userCompany: userProfile.partnerName || userProfile.tenantName || 'PropInvest HQ',
        userRole: userProfile.role,
        sourceOrigin: 'web',
        context: captureSubmissionContext(),
      });

      const ticket = await getTicketById(ticketId);
      setSubmitted(ticket);
      showToast(
        'success',
        ticket ? `Ticket ${ticket.ticketNumber} created` : 'Ticket created',
        'We will respond within 24 hours.',
      );
    } catch (err) {
      console.error('Error submitting ticket:', err);
      showToast('error', 'Failed to submit ticket. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="flex flex-col items-center gap-3 py-8 text-center">
        <CheckCircle className="h-10 w-10 text-green-500" />
        <p className="text-lg font-semibold text-neutral-900">
          Ticket {submitted.ticketNumber} created
        </p>
        <p className="max-w-sm text-sm text-neutral-500">
          Our team will respond within 24 hours. Track the conversation under{' '}
          <a href={`/my-tickets?ticket=${submitted.id}`} className="text-primary hover:underline">
            My Tickets
          </a>
          .
        </p>
        <button
          type="button"
          onClick={() => {
            setSubmitted(null);
            setSubject('');
            setDescription('');
            setScreenshots([]);
          }}
          className="secondary-button text-sm"
        >
          Submit another
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="mb-1 block text-xs font-medium text-neutral-600">Type</label>
          <select
            value={type}
            disabled={submitting}
            onChange={(e) => setType(e.target.value as TicketType)}
            className="input-field text-sm"
          >
            <option value="">Select…</option>
            {TYPE_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="mb-1 block text-xs font-medium text-neutral-600">Priority</label>
          <select
            value={priority}
            disabled={submitting}
            onChange={(e) => setPriority(e.target.value as TicketPriority)}
            className="input-field text-sm"
          >
            <option value={TicketPriority.LOW}>Low</option>
            <option value={TicketPriority.MEDIUM}>Medium</option>
            <option value={TicketPriority.HIGH}>High</option>
            <option value={TicketPriority.URGENT}>Urgent</option>
          </select>
        </div>
      </div>

      <div>
        <label className="mb-1 block text-xs font-medium text-neutral-600">Subject</label>
        <input
          type="text"
          value={subject}
          disabled={submitting}
          onChange={(e) => setSubject(e.target.value)}
          placeholder="One line summary"
          className="input-field text-sm"
        />
      </div>

      <div>
        <label className="mb-1 block text-xs font-medium text-neutral-600">Description</label>
        <textarea
          rows={4}
          value={description}
          disabled={submitting}
          onChange={(e) => setDescription(e.target.value)}
          placeholder="What happened, what did you expect, and what did you try?"
          className="input-field text-sm"
        />
      </div>

      <div>
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          multiple
          hidden
          onChange={(e) => {
            handleFiles(e.target.files);
            e.target.value = '';
          }}
        />
        <button
          type="button"
          disabled={submitting}
          onClick={() => fileInputRef.current?.click()}
          className="inline-flex items-center gap-1.5 rounded-md border border-dashed border-neutral-300 bg-neutral-50 px-3 py-1.5 text-xs font-medium text-neutral-700 hover:bg-neutral-100"
        >
          <ImageIcon className="h-3.5 w-3.5" /> Add screenshot
        </button>
        {screenshots.length > 0 && (
          <div className="mt-2 grid grid-cols-4 gap-2">
            {screenshots.map((s) => (
              <div key={s.id} className="group relative aspect-video overflow-hidden rounded-md border border-neutral-200">
                <img src={s.preview} alt="screenshot" className="h-full w-full object-cover" />
                <button
                  type="button"
                  onClick={() => {
                    URL.revokeObjectURL(s.preview);
                    setScreenshots((prev) => prev.filter((x) => x.id !== s.id));
                  }}
                  className="absolute right-1 top-1 rounded bg-white/90 p-0.5 text-red-600 opacity-0 group-hover:opacity-100"
                >
                  <Trash2 className="h-3 w-3" />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      <p className="text-[11px] text-neutral-400">
        Your name, role and the page you're on are included automatically so support can reproduce the issue.
      </p>

      <div className="flex justify-end">
        <button
          type="button"
          onClick={() => void submit()}
          disabled={submitting}
          className="primary-button flex items-center gap-1.5 text-sm"
        >
          {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
          Submit ticket
        </button>
      </div>
    </div>
  );
};

export default TicketFormTab;
